import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, Translations, translations, LOCALE_BY_LANGUAGE } from '../i18n';
import { useTenant } from './TenantContext';

const LANGUAGE_STORAGE_KEY = 'preferredLanguage';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translations;
  locale: string;
  isToggleEnabled: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const isLanguage = (value: string | null): value is Language =>
  value === 'en' || value === 'es';

const getStoredLanguage = (): Language => {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return isLanguage(stored) ? stored : 'en';
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { tenantConfig } = useTenant();
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(getStoredLanguage);

  const isToggleEnabled = tenantConfig?.tenant_id === 'evanston';
  // Only evanston offers Spanish; other tenants stay English even if a
  // preference is left over in localStorage from another tenant build.
  const language: Language = isToggleEnabled ? selectedLanguage : 'en';

  const setLanguage = (newLanguage: Language) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
    setSelectedLanguage(newLanguage);
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
    locale: LOCALE_BY_LANGUAGE[language],
    isToggleEnabled,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
